import { useEffect, useState } from "react";
import axios from "axios";
import {
  CalendarCheck,
  BadgeDollarSign,
  ShieldCheck,
  MapPin,
  LayoutDashboard,
  Headphones,
  PawPrint,
  Handshake,
  Store,
  HeartHandshake,
  Car,
  Wallet,
  Gift,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import RecepPetLogo from "@/components/RecepPetLogo";
import { resolveUrl } from "@/lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const REGISTER_URL = "https://receppet.com.br/register?type=provider";

const ICONS = [Wallet, Gift, LayoutDashboard, CalendarCheck, MapPin, Headphones, BadgeDollarSign, ShieldCheck];
const PROFILE_ICONS = [Store, HeartHandshake, Car];

const toEmbed = (url = "") => {
  if (!url) return "";
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return yt ? `https://www.youtube.com/embed/${yt[1]}` : url;
};

const isVideoFile = (url = "") => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

const trackClick = () => {
  axios.post(`${API}/cta-click`).catch(() => {});
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const CtaButton = ({ text, color, testId, className = "" }) => (
  <a
    href={REGISTER_URL}
    onClick={trackClick}
    data-testid={testId}
    className={`group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-bold text-white shadow-xl transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-2xl active:translate-y-0 ${className}`}
    style={{ backgroundColor: color, boxShadow: `0 18px 40px -14px ${color}` }}
  >
    {text}
    <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
  </a>
);

export default function Parceiros() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/landing-page`)
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({}));
  }, []);

  if (!config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#002B49]" data-testid="parceiros-loading">
        <PawPrint className="h-10 w-10 animate-pulse text-[#00B074]" />
      </div>
    );
  }

  const cta = config.ctaButtonText || "Quero ser parceiro";
  const ctaColor = config.ctaButtonColor || "#00A86B";
  const video = toEmbed(config.videoUrl);
  const benefits = (config.benefits || []).filter((b) => b.title || b.description);
  const partners = (config.featuredPartners || []).filter((p) => p.name).slice(0, 30);
  const highlight = config.partnershipHighlight || "";
  const profileCards = (config.profileCards || []).filter((c) => c.title || c.description);

  return (
    <div className="min-h-screen bg-[#F7FAF9] font-sans text-[#002B49]" data-testid="parceiros-page">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#002B49]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="rounded-xl bg-white px-3 py-1.5">
            <RecepPetLogo logoUrl={resolveUrl(config.logoUrl)} className="h-9" />
          </div>
          <a
            href={REGISTER_URL}
            onClick={trackClick}
            data-testid="header-cta-button"
            className="hidden rounded-full px-5 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90 sm:inline-flex"
            style={{ backgroundColor: ctaColor }}
          >
            {cta}
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#002B49] text-white">
        {config.heroBannerUrl && (
          <img
            src={resolveUrl(config.heroBannerUrl)}
            alt=""
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-25"
            data-testid="hero-banner-image"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[#002B49] via-[#0B3C5D]/90 to-[#00A86B]/25" />
        <div className="relative mx-auto grid max-w-6xl gap-14 px-5 py-16 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#00B074]/40 bg-[#00B074]/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#7CE9BE]">
              <PawPrint className="h-4 w-4" /> {config.heroBadge || "Para prestadores de serviços pet"}
            </span>
            <h1
              className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="hero-title"
            >
              {config.heroTitle}
            </h1>
            <p className="mt-6 max-w-xl text-base text-white/80 md:text-lg" data-testid="hero-subtitle">
              {config.heroSubtitle}
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <CtaButton text={cta} color={ctaColor} testId="hero-cta-button" className="w-full sm:w-auto" />
              <span className="text-sm text-white/60">Cadastro em menos de 3 minutos</span>
            </div>
            <ul className="mt-9 grid gap-3 text-sm text-white/75 sm:grid-cols-2">
              {["Sem taxa de adesão", "Clientes da sua região", "Painel completo do parceiro", "Suporte dedicado"].map(
                (item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#00B074]" /> {item}
                  </li>
                )
              )}
            </ul>
          </div>

          {video && (
            <div className="relative" data-testid="hero-video-wrapper">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#00B074]/40 to-transparent blur-2xl" />
              <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-black shadow-2xl">
                <div className="aspect-video w-full">
                  {isVideoFile(video) ? (
                    <video src={video} controls playsInline className="h-full w-full object-cover" data-testid="hero-video-file" />
                  ) : (
                    <iframe
                      src={video}
                      title="Vídeo RecepPet Parceiros"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="h-full w-full"
                      data-testid="hero-video-iframe"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PARTNERSHIP HIGHLIGHT */}
      {highlight && (
        <section className="relative z-10 mx-auto -mt-10 max-w-4xl px-5 lg:-mt-12" data-testid="partnership-highlight-section">
          <div className="rounded-3xl border-2 border-[#00A86B]/35 bg-white p-7 shadow-[0_24px_60px_-30px_rgba(11,60,93,0.55)] sm:p-9">
            <div className="flex items-start gap-4">
              <Handshake className="mt-0.5 h-8 w-8 shrink-0 text-[#00A86B]" />
              <p
                className="text-base font-semibold leading-relaxed text-[#0B3C5D] sm:text-lg"
                data-testid="partnership-highlight-text"
              >
                {highlight}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* PROFILE CARDS */}
      {profileCards.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 pt-16 lg:pt-24" data-testid="profile-cards-section">
          <h2 className="text-base font-semibold uppercase tracking-[0.2em] text-[#00A86B] md:text-lg">
            Quem pode ser nosso parceiro
          </h2>
          <p className="mt-4 max-w-2xl text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
            Três formas de ganhar com o RecepPet
          </p>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {profileCards.map((c, i) => {
              const Icon = PROFILE_ICONS[i % PROFILE_ICONS.length];
              return (
                <article
                  key={`${c.title}-${i}`}
                  data-testid={`profile-card-${i}`}
                  className="group relative overflow-hidden rounded-3xl border border-[#0B3C5D]/10 bg-gradient-to-br from-white to-[#EAF6F1] p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#00A86B]/45 hover:shadow-xl"
                >
                  <span className="absolute right-6 top-6 text-5xl font-black text-[#0B3C5D]/5">{i + 1}</span>
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00B074] to-[#0B3C5D]">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="mt-6 text-lg font-extrabold leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#0B3C5D]/75">{c.description}</p>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* BENEFITS */}
      {benefits.length > 0 && (
        <section className="mx-auto max-w-6xl px-5 py-16 lg:py-24" data-testid="benefits-section">
          <h2 className="max-w-2xl text-base font-semibold uppercase tracking-[0.2em] text-[#00A86B] md:text-lg">
            Por que ser parceiro RecepPet
          </h2>
          <p className="mt-4 max-w-2xl text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
            Sua vitrine profissional pronta para ser encontrada
          </p>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <article
                  key={`${b.title}-${i}`}
                  data-testid={`benefit-card-${i}`}
                  className="group rounded-2xl border border-[#0B3C5D]/10 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#00A86B]/40 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#00B074] to-[#0B3C5D]">
                    {b.iconUrl ? (
                      <img src={resolveUrl(b.iconUrl)} alt="" className="h-7 w-7 object-contain" />
                    ) : (
                      <Icon className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <h3 className="text-lg font-bold">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#0B3C5D]/75">{b.description}</p>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* FEATURED PARTNERS */}
      {partners.length > 0 && (
        <section className="border-y border-[#0B3C5D]/10 bg-white py-14 lg:py-20" data-testid="featured-partners-section">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="featured-partners-title">
              Já fazem parte do nosso time:
            </h2>
            <p className="mt-3 max-w-2xl text-base text-[#0B3C5D]/70 md:text-lg">
              Conheça alguns dos profissionais e estabelecimentos que já estão na rede RecepPet.
            </p>

            <div className="-mx-5 mt-10 overflow-x-auto px-5 pb-4 [scrollbar-width:thin] sm:mx-0 sm:px-0">
              <ul className="flex snap-x snap-mandatory gap-5 sm:grid sm:grid-cols-4 sm:gap-6 lg:grid-cols-6">
                {partners.map((p, i) => (
                  <li
                    key={`${p.name}-${i}`}
                    data-testid={`partner-item-${i}`}
                    className="group w-24 shrink-0 snap-start text-center sm:w-auto"
                  >
                    <div className="relative mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-[#00B074] to-[#0B3C5D] p-[3px] transition-transform duration-300 group-hover:scale-105 sm:h-24 sm:w-24">
                      <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-white">
                        {p.imageUrl ? (
                          <img
                            src={resolveUrl(p.imageUrl)}
                            alt={p.name}
                            loading="lazy"
                            className={
                              p.imageFit === "cover"
                                ? "h-full w-full rounded-full object-cover"
                                : "h-[78%] w-[78%] rounded-full object-contain"
                            }
                          />
                        ) : (
                          <span className="text-lg font-extrabold text-[#0B3C5D]">{initials(p.name)}</span>
                        )}
                      </div>
                    </div>
                    <p className="mt-3 truncate text-xs font-bold leading-tight text-[#002B49] sm:text-sm">{p.name}</p>
                    {p.category && <p className="mt-0.5 truncate text-[11px] font-semibold text-[#00A86B]">{p.category}</p>}
                    {p.location && <p className="truncate text-[11px] text-[#0B3C5D]/55">{p.location}</p>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-[#0B3C5D] py-16 text-white lg:py-20" data-testid="final-cta-section">
        <div className="mx-auto max-w-4xl px-5 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Garanta sua vaga como parceiro pioneiro!
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/75 md:text-lg">
            Faça seu cadastro gratuito e prepare seu perfil para a grande divulgação.
          </p>
          <div className="mt-9 flex justify-center">
            <CtaButton text={cta} color={ctaColor} testId="final-cta-button" />
          </div>
        </div>
      </section>

      <footer className="bg-[#002B49] py-8 text-center text-sm text-white/50">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-5">
          <div className="rounded-xl bg-white px-3 py-1.5">
            <RecepPetLogo logoUrl={resolveUrl(config.logoUrl)} className="h-8" />
          </div>
          <p>© {new Date().getFullYear()} RecepPet · Todos os direitos reservados</p>
        </div>
      </footer>
    </div>
  );
}
